import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { apiClient } from "@/lib/api";

/**
 * Sticky warning strip shown under the topbar whenever the logged-in
 * user has an active impersonation session. Only polls /current when
 * there has been a session in this tab's lifetime; no active session
 * = no polling (idle users don't hit the API every 30s).
 *
 * Clicking "Stop" hits /admin/v1/impersonation/{id}/stop and
 * invalidates the query on success.
 *
 * Positioning: does NOT use \`sticky top-[56px]\` because the topbar
 * can flex-wrap to two lines on narrow phones. Instead we stack
 * \`sticky top-0\` directly below the topbar in the document flow, and
 * the shell renders us inline after the header - natural document
 * order keeps us pinned just under whatever height the topbar ends up.
 */
export function ImpersonationBanner(): JSX.Element | null {
  const queryClient = useQueryClient();

  // Seed with a one-shot check; poll every 30s only AFTER we've seen a
  // session (cheap idle users never hit /current repeatedly). Also
  // disable window-focus / reconnect refetches until we've seen one,
  // so tab switches on a fresh session don't regen traffic either.
  const seenOnceRef = useRef(false);
  const { data } = useQuery({
    queryKey: ["impersonation", "current"],
    queryFn: () => apiClient.getCurrentImpersonation(),
    retry: false,
    refetchInterval: () => (seenOnceRef.current ? 30_000 : false),
    refetchOnWindowFocus: seenOnceRef.current,
    refetchOnReconnect: seenOnceRef.current,
  });

  const stop = useMutation({
    mutationFn: (id: string) => apiClient.stopImpersonation(id),
    onSuccess: () => {
      // Drop the current-session cache AND the acting-as profile so
      // the sidebar / button gates swap back to the actor's view
      // without a reload. Without the second invalidation the UI
      // would keep rendering as the target until the acting-as
      // query's staleTime (60s) elapsed - the explicit invalidate
      // makes the swap instant.
      void queryClient.invalidateQueries({ queryKey: ["impersonation", "current"] });
      void queryClient.invalidateQueries({ queryKey: ["profile", "acting-as"] });
    },
  });

  const session = data?.session;

  // Mutate the ref inside a commit-phase effect - mutating during render
  // is technically legal but double-invoked under StrictMode and makes
  // the side-effect non-obvious to readers.
  useEffect(() => {
    if (session) seenOnceRef.current = true;
  }, [session]);

  // Live countdown ticks every second but only while a session is
  // active; no idle timer when null.
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    if (!session) return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [session]);

  if (!session) return null;

  const expiresMs = new Date(session.expires_at).getTime();
  const remainingSec = Math.max(0, Math.round((expiresMs - nowMs) / 1000));
  const mins = Math.floor(remainingSec / 60);
  const secs = remainingSec % 60;

  return (
    <div
      // role="status" + aria-live="polite" - `alert` would make every
      // screen reader announce the 1s-tick countdown. Polite status
      // only re-announces meaningful state changes (banner shows/hides).
      role="status"
      aria-live="polite"
      aria-atomic="false"
      className="sticky top-0 z-[9] flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-300/60 bg-amber-100 px-3 py-2 text-sm text-amber-900 dark:border-amber-400/40 dark:bg-amber-900/40 dark:text-amber-100 sm:px-4"
    >
      <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        Impersonating <span className="break-words font-medium">{session.target.email}</span>
        {/* aria-hidden on the countdown - we don't want assistive
            tech reading "fifty-nine seconds, fifty-eight seconds,
            ..." every second. */}
        <span className="ml-2 font-mono text-xs opacity-75" aria-hidden="true">
          {mins}:{String(secs).padStart(2, "0")} remaining
        </span>
      </span>
      <button
        type="button"
        onClick={() => stop.mutate(session.id)}
        disabled={stop.isPending}
        className="flex flex-shrink-0 items-center gap-1.5 rounded-md border border-amber-400/60 bg-amber-200/60 px-2 py-1 text-xs font-medium hover:bg-amber-200 disabled:opacity-50 dark:border-amber-400/40 dark:bg-amber-800/60 dark:hover:bg-amber-800"
      >
        <LogOut className="h-3.5 w-3.5" />
        {stop.isPending ? "Stopping…" : "Stop impersonating"}
      </button>
    </div>
  );
}
